import React, { useCallback } from 'react';
import { useNotifications } from '../../feedback/NotificationProvider';
import { OrderTag } from '../OrderTag';
import { ItemTag } from '../ItemTag';
import type { OrderItemTagProps, TagSize } from './OrderItemTag.types';

const sizeClasses: Record<TagSize, string> = {
  xs: 'text-xs px-1.5 py-0.5',
  sm: 'text-sm px-2 py-0.5',
  md: 'text-base px-2.5 py-1'
};

/**
 * OrderItemTag - Exibe IDs combinados [O-XXX/I-XXX] ou separados
 * 
 * Variantes:
 * - default: Duas tags separadas <OrderTag /> <ItemTag />
 * - compact: Uma tag combinada [O-XXX/I-XXX]
 */
export const OrderItemTag: React.FC<OrderItemTagProps> = ({
  orderId,
  itemId,
  size = 'sm',
  variant = 'default',
  copyable = true,
  className = ''
}) => {
  const { notify } = useNotifications();

  const handleCopyCompact = useCallback(async (e: React.MouseEvent) => {
    if (!copyable) return;
    
    e.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(`${orderId}-${itemId}`);
      notify({
        type: 'success',
        title: 'IDs Copiados',
        message: `O-${orderId}/I-${itemId} copiado para área de transferência`,
        sticky: false
      });
    } catch (err) {
      notify({
        type: 'error',
        title: 'Erro ao copiar',
        message: 'Não foi possível copiar os IDs',
        sticky: false
      });
    }
  }, [orderId, itemId, copyable, notify]);

  // Variante compacta: uma tag combinada
  if (variant === 'compact') {
    return (
      <span
        className={`
          inline-flex items-center font-mono font-semibold 
          rounded border select-none
          bg-navy-700/60 text-gray-200 border-navy-600/60
          ${copyable ? 'cursor-pointer hover:bg-navy-700/80 hover:border-navy-500/70 active:scale-95' : ''}
          transition-all duration-150
          ${sizeClasses[size]}
          ${className}
        `}
        title={copyable ? `Pedido #${orderId} • Item #${itemId} (clique para copiar)` : `Pedido #${orderId} • Item #${itemId}`}
        onClick={copyable ? handleCopyCompact : undefined}
        role={copyable ? 'button' : undefined}
        tabIndex={copyable ? 0 : undefined}
      >
        <span className="text-yellow-300">O-{orderId}</span>
        <span className="opacity-40 mx-0.5">/</span>
        <span className="text-amber-300">I-{itemId}</span>
      </span>
    );
  }

  // Variante padrão: duas tags separadas
  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <OrderTag orderId={orderId} size={size} copyable={copyable} />
      <ItemTag itemId={itemId} size={size} copyable={copyable} />
    </div>
  );
};
